import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { SelectField } from "components/AddVaccineForm/components";

import style from "../AddVaccineForm.module.css";
import {
  ADMINISTRATION_METHODS,
  CLINICAL_NECESSITY_OPTIONS,
  CONSENT_OPTIONS,
  DEFAULT_VALUES,
  DOSE_COURSE_OPTIONS,
  LEGACY_ADMINISTRATION_METHOD_CODES,
  LEGACY_SITE_GIVEN_CODES,
  RSV_SITE_CODES,
  RSV_VACCINE_TYPE,
  SITE_OPTIONS,
} from "../constants";
import { SearchableSelectField } from "./SearchableSelectField";
import { TextField } from "./TextField";
import { VACCINE_MODE } from "./VaccineModeSwitch";

const withFallback = (options, value, isManualEdit) => {
  if (!isManualEdit || !value) return options;
  const exists = options.some((o) => o.value === value);
  if (exists) return options;
  return [...options, { label: value, value }];
};

export const AdministrationSection = ({
  doctors,
  isLoadingDoctors,
  mode,
  fallbackVaccinatorName,
  isEditMode,
  derivedRouteCode = "",
}) => {
  const { watch, setValue } = useFormContext();
  const vaccineType = watch("type");
  const clinicallyNecessary = watch("clinicallyNecessary");
  const isManualEdit = isEditMode && mode === VACCINE_MODE.MANUAL;
  const isGivenElsewhere = watch("givenElsewhere");
  const currentSiteGiven = watch("siteGiven");
  const currentAdminMethod = watch("administrationMethod");
  // The route is a property of the product, not a choice, and offering all four for
  // every vaccine is how dev ended up with an oral Rotavirus recorded as IM and a
  // nasal Fluenz as IM (KIN-1679). Only RSV is narrowed here: nirsevimab is
  // intramuscular, and Phase 2 §4.4.2 states IM must be the only option for it. The
  // other programmes keep the full list on purpose — a blanket IM-only rule would make
  // the genuinely oral and nasal vaccines unrecordable, which is the same class of bug
  // pointing the other way.
  const isRsvType = vaccineType === RSV_VACCINE_TYPE;
  /*
   * Where the catalogue states the product's route it is filled in on selection, so the
   * field is a derived value and editing it can only produce a dose the backend will
   * refuse — ROUTE_DOES_NOT_MATCH_PRODUCT is a HARD_STOP with no way past it
   * (KIN-2115). Locked only for the products we hold a route for; the rest stay a
   * choice, as a blanket lock would make the genuinely unstated ones unrecordable.
   * Manual mode is left alone: it records what another practice did, not what we are
   * about to submit.
   */
  const isRouteDerivedFromProduct =
    mode === VACCINE_MODE.HEALTHLINK && !!derivedRouteCode;
  const availableAdminMethods = isRsvType
    ? ADMINISTRATION_METHODS.filter((method) => method.value === "IM")
    : ADMINISTRATION_METHODS;
  // Narrowing the route to IM but leaving every site on offer let the form record an
  // intramuscular dose into the mouth or the nares. Nirsevimab goes into a limb — the
  // anterolateral thigh in an infant, which is already the default — so the sites that
  // contradict the route are removed rather than left to be chosen by mistake.
  const availableSites = isRsvType
    ? SITE_OPTIONS.filter((site) => RSV_SITE_CODES.includes(site.value))
    : SITE_OPTIONS;
  const siteOptions = withFallback(availableSites, currentSiteGiven, isManualEdit);
  const adminMethodOptions = withFallback(availableAdminMethods, currentAdminMethod, isManualEdit);
  const vaccinatorId = watch("vaccinator");

  const doseNumberCode = watch("doseNumberCode");
  const isHealthlinkMode = mode === VACCINE_MODE.HEALTHLINK;
  const isCovidType =
    typeof vaccineType === "string" &&
    vaccineType.toLowerCase().startsWith("covid-19");
  const isPneumococcalType =
    vaccineType === "Pneumococcal" || vaccineType === "Pneumococcol";
  const isInfluenzaType = vaccineType === "Influenza";
  const isDose1 = doseNumberCode === "1";

  const isFluOrPneumoHealthlink =
    isHealthlinkMode && (isPneumococcalType || isInfluenzaType);

  // Dose 1 → Course Completion; Dose 2+ → Clinical Necessity.
  const showCourseCompletion = isFluOrPneumoHealthlink && isDose1;
  const showClinicalNecessity = isFluOrPneumoHealthlink && !isDose1;

  // A record written before February 2026 holds its route as a display label, which no
  // dropdown here offers: in HealthLink mode the field renders empty, and in manual mode
  // withFallback keeps the label as its own value so saving writes the invalid encoding
  // straight back out to RXR.1 (KIN-1680). Translating it to the 0162 code means opening
  // an old record repairs it instead.
  useEffect(() => {
    const codeForLegacyLabel =
      LEGACY_ADMINISTRATION_METHOD_CODES[currentAdminMethod];

    if (codeForLegacyLabel) {
      setValue("administrationMethod", codeForLegacyLabel, {
        shouldValidate: true,
      });
    }
  }, [currentAdminMethod, setValue]);

  // Same treatment for the site: a record written before the encoding switch holds a
  // display label, which the picker cannot offer, so the field renders empty. Only the
  // unambiguous labels are translated — the ones carrying no laterality are left as they
  // are rather than having a side invented for them (KIN-1680).
  useEffect(() => {
    const codeForLegacySite = LEGACY_SITE_GIVEN_CODES[currentSiteGiven];

    if (codeForLegacySite) {
      setValue("siteGiven", codeForLegacySite, { shouldValidate: true });
    }
  }, [currentSiteGiven, setValue]);

  // Narrowing the options is not enough on its own: switching to RSV while a route
  // carried over from another product is selected would leave the field holding a value
  // its own dropdown no longer offers, which reads as an empty required field.
  useEffect(() => {
    if (isRsvType && currentAdminMethod !== "IM") {
      setValue("administrationMethod", "IM", { shouldValidate: true });
    }
  }, [isRsvType, currentAdminMethod, setValue]);

  // Same reason as the route above: a site carried over from another product would sit
  // in the field without being offered by its own dropdown. Falls back to the thigh the
  // form already defaults to for a new record.
  useEffect(() => {
    if (isRsvType && !RSV_SITE_CODES.includes(currentSiteGiven)) {
      setValue("siteGiven", DEFAULT_VALUES.siteGiven, { shouldValidate: true });
    }
  }, [isRsvType, currentSiteGiven, setValue]);

  useEffect(() => {
    if (!isHealthlinkMode || isCovidType) {
      setValue("administrationNotesCode", "", { shouldValidate: false });
      return;
    }

    if (showCourseCompletion) {
      // Default pneumococcal dose 1 to "completes course"; clear any
      // clinical-necessity code carried over from a dose-2 selection.
      const current = watch("administrationNotesCode");
      if (current === "X0070-3" || current === "X0070-4") {
        setValue("administrationNotesCode", "", { shouldValidate: false });
      } else if (isPneumococcalType && !isEditMode && !current) {
        setValue("administrationNotesCode", "X0070-1", { shouldValidate: true });
      }
    }
  }, [
    isHealthlinkMode,
    isCovidType,
    isPneumococcalType,
    isEditMode,
    showCourseCompletion,
    vaccineType,
    setValue,
    watch,
  ]);

  const clinicalNecessityValue = clinicallyNecessary === true ? "yes" : clinicallyNecessary === false ? "no" : "";

  const getVaccinatorName = () => {
    if (isLoadingDoctors) return "Loading...";
    if (!vaccinatorId) return "";
    const doctor = doctors?.find((d) => d.id.toString() === vaccinatorId);

    if (doctor) return `${doctor.firstName} ${doctor.lastName}`;

    return fallbackVaccinatorName || "";
  };

  const gridClass =
    showCourseCompletion || showClinicalNecessity
      ? style.inputRowFive
      : style.inputRow;

  return (
    <>
      <hr className={style.sectionDivider} />
      <h6 className={style.sectionHeader}>Administration</h6>

      <div className={gridClass}>
        {/* Who gave it, into what, by which route — all three describe an administration
            this practice performed. On a dose a school or another country gave, nobody has
            the answers, and asking is what made the ordinary path unusable for it
            (KIN-1883). Consent stays: it is still worth recording what the parent agreed
            to, whoever held the syringe. */}
        {!isGivenElsewhere && (
          <>
            <TextField
              name="vaccinatorDisplay"
              label="Vaccinator"
              value={getVaccinatorName()}
              disabled
            />

            <SearchableSelectField
              name="siteGiven"
              label="Site given"
              options={siteOptions}
              placeholder="Search site..."
              required
            />

            <SearchableSelectField
              name="administrationMethod"
              label="Administration method"
              options={adminMethodOptions}
              placeholder="Search method..."
              required
              disabled={isRouteDerivedFromProduct}
            />
          </>
        )}

        <SelectField
          name="consentGivenCode"
          label="Consent"
          options={CONSENT_OPTIONS}
          placeholder="Select"
          required
        />

        {/* Beside Consent rather than up with the checkbox: both describe the
            circumstances of the dose rather than the product, and it reads as one
            thought. Free text on purpose — a closed list would be wrong within a month,
            and the cases this exists for are the ones nobody anticipated (KIN-1883). */}
        {!!isGivenElsewhere && (
          <TextField
            name="givenElsewhereBy"
            label="Given by"
            placeholder="e.g. maternity unit, school programme"
          />
        )}

        {showCourseCompletion && (
          <SelectField
            name="administrationNotesCode"
            label="Course Completion"
            options={DOSE_COURSE_OPTIONS}
            placeholder="Select code"
            required
          />
        )}

        {showClinicalNecessity && (
          <SelectField
            name="clinicalNecessityDisplay"
            label="Clinical Necessity"
            options={CLINICAL_NECESSITY_OPTIONS}
            value={clinicalNecessityValue}
            onChange={(val) => setValue("clinicallyNecessary", val === "yes", { shouldValidate: true })}
            placeholder="Select"
            required
          />
        )}
      </div>
    </>
  );
};
